import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { getStoredUser, clearStoredUser } from '../utils/safeStorage';

const NAV_LINKS = [
  { label: 'Home', page: 'landing' },
  { label: 'Packages', page: 'category', data: { category: 'umrah-packages' } },
  { label: 'Flights', page: 'flights' },
  { label: 'Shop', page: 'shop' },
  { label: 'Blog', page: 'blog' },
  { label: 'About', page: 'about' },
  { label: 'Contact', page: 'contact' },
];

const Navbar = ({ navigate, transparent = false }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const { getCartCount } = useCart();
  const cartCount = getCartCount();
  const lightText = transparent && !isScrolled;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 16);
    window.addEventListener('scroll', handleScroll);
    setUser(getStoredUser());
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    clearStoredUser();
    setUser(null);
    setIsSidebarOpen(false);
    navigate('landing');
  };

  const closeSidebar = () => setIsSidebarOpen(false);

  const go = (page, data) => {
    navigate(page, data);
    closeSidebar();
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        lightText ? 'bg-transparent py-4' : 'bg-cream/95 backdrop-blur-md shadow-[0_8px_30px_rgba(18,28,26,0.08)] py-2.5 border-b border-sand'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-4">
            <button onClick={() => navigate('landing')} className="flex items-center group shrink-0">
              <img
                src="/logo.jpeg"
                alt="TM Fouzy Travel"
                className="h-10 w-auto sm:h-12 md:h-14 lg:h-16 object-contain transform group-hover:scale-105 transition-transform rounded-md"
              />
            </button>

            <div className="hidden xl:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => navigate(link.page, link.data)}
                  className={`nav-link px-3 py-2 text-sm font-medium transition-all ${
                    lightText
                      ? 'text-white/90 hover:text-accent'
                      : 'text-ink/80 hover:text-primary'
                  }`}>
                  {link.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate('cart')}
                aria-label="Shopping cart"
                className={`relative p-2 rounded-lg transition-all ${
                  lightText ? 'text-white hover:bg-white/10' : 'text-ink/80 hover:bg-cream hover:text-primary'
                }`}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>

              {user ? (
                <div className="hidden xl:flex items-center gap-2">
                  <span className={`text-sm font-medium max-w-[140px] truncate ${
                    lightText ? 'text-white' : 'text-ink/80'
                  }`}>
                    Hi, {user.name || user.email}
                  </span>
                  <button
                    onClick={() => navigate('portal')}
                    className="gradient-gold hover:opacity-90 text-ink px-4 py-2 rounded-md transition-all font-semibold shadow-sm text-sm">
                    Dashboard
                  </button>
                </div>
              ) : (
                <div className="hidden xl:flex items-center gap-2">
                  <button
                    onClick={() => navigate('login')}
                    className={`px-4 py-2 rounded-md transition-all font-medium text-sm ${
                      lightText ? 'text-white hover:text-accent' : 'text-ink hover:text-primary'
                    }`}>
                    Login
                  </button>
                  <button
                    onClick={() => navigate('register')}
                    className="gradient-gold hover:opacity-90 text-ink px-5 py-2 rounded-md transition-all font-semibold shadow-sm text-sm">
                    Register
                  </button>
                </div>
              )}

              <button
                onClick={() => setIsSidebarOpen(true)}
                aria-label="Open menu"
                className={`p-2 rounded-lg transition-all ${
                  lightText ? 'hover:bg-white/10' : 'hover:bg-cream'
                }`}>
                <svg className="w-6 h-6" fill="none" stroke={lightText ? '#ffffff' : '#1C2421'} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-50" onClick={closeSidebar} />
      )}

      <div className={`fixed top-0 right-0 h-full w-80 max-w-[min(90vw,20rem)] bg-cream shadow-2xl z-[60] transform transition-transform duration-300 ease-in-out pt-[env(safe-area-inset-top)] ${
        isSidebarOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-5 border-b border-sand">
            <h2 className="text-lg font-bold text-ink">Menu</h2>
            <button onClick={closeSidebar} className="text-ink/50 hover:text-ink p-2 hover:bg-sand rounded-lg" aria-label="Close menu">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {user && (
            <div className="p-5 bg-cream border-b">
              <p className="text-xs text-primary mb-0.5">Signed in as</p>
              <p className="font-semibold text-ink truncate">{user.name || user.email}</p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {[
                { icon: '🏠', label: 'Home', page: 'landing' },
                { icon: 'ℹ️', label: 'About Us', page: 'about' },
              ].map((item) => (
                <button key={item.page} onClick={() => go(item.page)}
                  className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                  <span>{item.icon}</span><span>{item.label}</span>
                </button>
              ))}

              <p className="text-[11px] font-semibold text-ink/40 uppercase tracking-wider px-4 pt-4 pb-1">Packages</p>
              {[
                { icon: '🕋', label: 'Umrah Packages', page: 'category', data: { category: 'umrah-packages' } },
                { icon: '🌙', label: 'Hajj Packages', page: 'category', data: { category: 'hajj-packages' } },
                { icon: '🏛️', label: 'Ziarah Tours', page: 'category', data: { category: 'ziarah-tours' } },
                { icon: '✈️', label: 'Holiday Packages', page: 'category', data: { category: 'holiday-packages' } },
              ].map((item) => (
                <button key={item.label} onClick={() => go(item.page, item.data)}
                  className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                  <span>{item.icon}</span><span>{item.label}</span>
                </button>
              ))}

              <button onClick={() => go('shop')}
                className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                <span>🛍️</span><span>Shop</span>
              </button>
              <button onClick={() => go('flights')}
                className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                <span>✈️</span><span>Flight Schedule</span>
              </button>
              <button onClick={() => go('blog')}
                className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                <span>📰</span><span>Blog &amp; News</span>
              </button>
              <button onClick={() => go('contact')}
                className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                <span>📞</span><span>Contact Us</span>
              </button>
              {user && (
                <button onClick={() => go('portal')}
                  className="w-full text-left text-ink/80 hover:text-primary hover:bg-white px-4 py-3 rounded-lg font-medium flex items-center gap-3">
                  <span>👤</span><span>My Dashboard</span>
                </button>
              )}

              <div className="pt-4 border-t border-sand mt-2">
                <button onClick={() => go('admin-login')}
                  className="w-full text-left text-ink/30 hover:text-ink/60 hover:bg-white px-4 py-2 rounded-lg text-xs flex items-center gap-3 transition-colors">
                  <span>🔐</span><span>Admin Login</span>
                </button>
              </div>
            </div>
          </div>

          <div className="p-5 border-t border-sand bg-white">
            {user ? (
              <button onClick={handleLogout}
                className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-3 rounded-lg font-semibold transition-all">
                Logout
              </button>
            ) : (
              <div className="space-y-2">
                <button onClick={() => go('login')}
                  className="w-full bg-white border border-primary text-primary hover:bg-cream px-4 py-3 rounded-md font-semibold">
                  Login
                </button>
                <button onClick={() => go('register')}
                  className="w-full gradient-gold hover:opacity-90 text-ink px-4 py-3 rounded-md font-semibold">
                  Register
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
